var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import debounce from 'lodash/debounce';
import * as Styled from './SelectSlide.styled';
/**
 * This component was once SlidingSelector and SlidingSelector.Option
 *
 * A `SelectSlide` is a container for `SelectSlide.Option`,
 * which displays a list of adjacent options with a sliding animation onto the selected option.
 *
 * `SelectSlide` is provides functionality equivalent to a "toggle" when there are only two options.
 */
var SelectSlide = function (_a) {
    var _b = _a.ariaLabel, ariaLabel = _b === void 0 ? null : _b, children = _a.children, className = _a.className, _c = _a.colorMode, colorMode = _c === void 0 ? 'day' : _c, name = _a.name, onChange = _a.onChange, selectedOption = _a.selectedOption, isFullWidth = _a.isFullWidth, _d = _a.optionPadding, optionPadding = _d === void 0 ? 'comfortable' : _d;
    var _e = useState(false), isReady = _e[0], setIsReady = _e[1];
    var slideRefs = useRef(new Map());
    var _f = useState([0, 0]), selectedMeasurements = _f[0], setSelectedMeasurements = _f[1];
    var currentWidth = selectedMeasurements[0], currentOffset = selectedMeasurements[1];
    useEffect(function () {
        // Enable CSS transition only after initial mount and
        // transition has performed to prevent animation of initial width
        setTimeout(function () { return setIsReady(true); }, Styled.SLIDE_TRANSITION);
    }, []);
    // Supports dynamic resizing of the SlideMarker when the viewport size changes
    useEffect(function () {
        var debouncedReady = debounce(function () { return setIsReady(true); }, 500);
        var handleResize = function () {
            var currentOption = slideRefs.current.get(selectedOption);
            if (currentOption) {
                var offsetWidth_1 = currentOption.offsetWidth, offsetLeft_1 = currentOption.offsetLeft;
                // Disables animation while allowing SlideMarker to adjust to viewport width
                flushSync(function () {
                    setIsReady(false);
                    setSelectedMeasurements([offsetWidth_1, offsetLeft_1]);
                });
                debouncedReady();
            }
        };
        if (isFullWidth) {
            window.addEventListener('resize', handleResize);
        }
        return function () {
            debouncedReady.cancel();
            window.removeEventListener('resize', handleResize);
        };
    }, [isFullWidth]);
    useEffect(function () {
        var currentOption = slideRefs.current.get(selectedOption);
        if (currentOption) {
            var rect = currentOption.getBoundingClientRect();
            var isInViewport = rect.top >= 0 &&
                rect.bottom <=
                    (window.innerHeight || document.documentElement.clientHeight);
            var offsetWidth = currentOption.offsetWidth, offsetLeft = currentOption.offsetLeft;
            if (isInViewport) {
                currentOption.scrollIntoView({
                    block: 'nearest',
                    inline: 'center'
                });
            }
            setSelectedMeasurements([offsetWidth, offsetLeft]);
        }
    }, [selectedOption]);
    return (React.createElement(Styled.SelectSlideContainer, { className: className },
        React.createElement(Styled.SelectSlide, { isFullWidth: isFullWidth, "aria-labelledby": name, className: className, role: "radiogroup", colorMode: colorMode },
            React.createElement(Styled.Label, { id: name }, ariaLabel || 'sliding selector'),
            React.Children.map(children, function (child, index) {
                return React.cloneElement(child, {
                    colorMode: colorMode,
                    isSelected: child.props.value === selectedOption,
                    name: name,
                    onChange: function (event) { return onChange(event, index); },
                    optionPadding: optionPadding,
                    ref: function (el) {
                        slideRefs.current.set(child.props.value, el);
                    }
                });
            }),
            React.createElement(Styled.SlideMarker, { "data-testid": "slidingSelectorMarker", width: currentWidth, offset: currentOffset, isReady: isReady, colorMode: colorMode }))));
};
/**
 * A `SelectSlide.Option` is used to display a single option within a `SelectSlide`.
 */
SelectSlide.Option = React.forwardRef(function (_a, ref) {
    var _b = _a.colorMode, colorMode = _b === void 0 ? 'day' : _b, _c = _a.isSelected, isSelected = _c === void 0 ? false : _c, _d = _a.label, label = _d === void 0 ? null : _d, _e = _a.name, name = _e === void 0 ? undefined : _e, _f = _a.onChange, onChange = _f === void 0 ? undefined : _f, value = _a.value, subcopy = _a.subcopy, _g = _a.optionPadding, optionPadding = _g === void 0 ? 'comfortable' : _g, props = __rest(_a, ["colorMode", "isSelected", "label", "name", "onChange", "value", "subcopy", "optionPadding"]);
    var displayValue = label || value;
    return (React.createElement(Styled.SlideOption, __assign({ ref: ref, isSelected: isSelected, colorMode: colorMode, isTwoLines: subcopy === null || subcopy === void 0 ? void 0 : subcopy.isTwoLines, optionPadding: optionPadding }, props),
        React.createElement(Styled.SlideOptionInput, { name: name, type: "radio", value: value, onChange: onChange }),
        React.createElement(Styled.SlideOptionSpan, null, displayValue),
        (subcopy === null || subcopy === void 0 ? void 0 : subcopy.text) && (React.createElement(Styled.SlideOptionSubcopy, null, subcopy.text))));
});
export default SelectSlide;
